import {
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
  Loader,
  Package2,
  Truck,
} from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useAwbTracking } from "../../hooks/useAwbTracking";
import { formatDate } from "../../utils/format";

function OrdersHeader() {
  const {
    currentSession,
    isTracking,
    error,
    startTracking,
    stopPolling,
    clearError,
  } = useAwbTracking();

  const handleStartTracking = async () => {
    clearError();
    try {
      await startTracking();
      toast.success("Sesiunea de tracking AWB a fost pornită");
    } catch (error) {
      toast.error("Eroare la pornirea tracking-ului AWB");
      console.error("Tracking error:", error);
    }
  };

  const handleStopTracking = () => {
    stopPolling();
    toast.success("Tracking AWB oprit");
  };

  const getTrackingStatusBadge = () => {
    if (!currentSession) return null;

    const statusConfig = {
      RUNNING: {
        text: "În progres",
        className: "bg-blue-100 text-blue-800",
        icon: <Loader className="w-3 h-3 animate-spin" />,
      },
      COMPLETED: {
        text: "Finalizat",
        className: "bg-green-100 text-green-800",
        icon: <CheckCircle className="w-3 h-3" />,
      },
      FAILED: {
        text: "Eșuat",
        className: "bg-red-100 text-red-800",
        icon: <AlertCircle className="w-3 h-3" />,
      },
    };

    const config = statusConfig[currentSession.status];

    return (
      <div className="flex items-center gap-2">
        <Badge className={`${config.className} flex items-center gap-1`}>
          {config.icon}
          {config.text}
        </Badge>
        {currentSession.status === "COMPLETED" && (
          <span className="text-sm text-gray-600">
            {currentSession.totalAwbsChecked} AWB-uri verificate,
            {currentSession.totalStatusesChanged} actualizări
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-red-600" />
          <h1 className="text-2xl font-bold text-gray-900">Comenzi</h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Tracking Status */}
          {getTrackingStatusBadge()}

          {/* AWB Tracking Button */}
          <Button
            className={`flex items-center gap-2 ${
              isTracking
                ? "bg-orange-600 hover:bg-orange-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            onClick={isTracking ? handleStopTracking : handleStartTracking}
            disabled={currentSession?.status === "RUNNING"}
          >
            <Truck className="w-4 h-4" />
            {isTracking ? "Oprește Tracking" : "Verifică statusurile AWB-uri"}
          </Button>

          {/* Export Button */}
          <Button
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            onClick={async () => {
              try {
                const response = await fetchWithAuth(
                  "/api/admin/orders/export",
                  {
                    method: "GET",
                  }
                );
                if (!response.ok)
                  throw new Error("Eroare la exportul comenzilor");

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `comenzi-${formatDate(new Date())}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                toast.success("Export realizat cu succes");
              } catch (error) {
                toast.error("Eroare la exportul comenzilor");
                console.error("Export error:", error);
              }
            }}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearError}
              className="ml-auto text-red-600 hover:text-red-700"
            >
              ×
            </Button>
          </div>
        </div>
      )}

      {/* Session Details */}
      {currentSession && currentSession.status !== "RUNNING" && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <div className="text-sm text-gray-600">
            <div className="flex justify-between items-center">
              <span>
                Sesiune #{currentSession.id} -{" "}
                {formatDate(new Date(currentSession.startedAt))}
              </span>
              {currentSession.completedAt && (
                <span>
                  Finalizat: {formatDate(new Date(currentSession.completedAt))}
                </span>
              )}
            </div>
            {currentSession.error && (
              <div className="mt-1 text-red-600">
                Eroare: {currentSession.error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersHeader;
